import type { PublicTopic } from './browseTypes';
import { browseViewScopeKey } from './topicBrowseViewCache';
import type { PrototypeRuntime, Root } from './prototypeTypes';
import type { BrowseSort } from './browseData';

const TTL_MS = 2 * 60 * 60 * 1000;
const MAX_SESSIONS = 12;
const MAX_HISTORY_ENTRIES = 24;

export interface BrowsePaginationSession {
	id: string;
	scopeKey: string;
	topics: PublicTopic[];
	nextCursor: string | null;
	hasMore: boolean;
	rankingVersion?: string;
	startedAt: number;
	scrollY: number;
}

export interface BrowseReturnSessionMarker {
	id: string;
	scopeKey: string;
	capturedAt: number;
}

export interface BrowseHistorySnapshot {
	entryId?: string;
	filter: string;
	search: string;
	sort: BrowseSort;
	scrollY: number;
}

const sessions = new Map<string, BrowsePaginationSession>();
const historyEntries = new Map<string, { snapshot: BrowseHistorySnapshot; marker: BrowseReturnSessionMarker | null; updatedAt: number }>();
const consumedReturnMarkers = new Set<string>();
let activeSessionIds = new WeakMap<Root, string>();
let fallbackId = 0;

export function browsePaginationScopeKey(runtime: PrototypeRuntime, filter: string, search: string, sort: BrowseSort): string {
	return browseViewScopeKey(runtime, filter, search, sort);
}

export function createBrowsePaginationSession(
	root: Root,
	input: Omit<BrowsePaginationSession, 'id' | 'startedAt' | 'scrollY'>,
	now = Date.now(),
): BrowsePaginationSession {
	pruneExpired(now);
	const session: BrowsePaginationSession = {
		id: nextSessionId(),
		scopeKey: input.scopeKey,
		topics: cloneTopics(input.topics),
		nextCursor: input.nextCursor,
		hasMore: input.hasMore,
		rankingVersion: input.rankingVersion,
		startedAt: now,
		scrollY: 0,
	};
	sessions.set(session.id, session);
	activeSessionIds.set(root, session.id);
	pruneOverflow();
	return cloneSession(session);
}

export function updateBrowsePaginationSession(
	root: Root,
	input: Pick<BrowsePaginationSession, 'topics' | 'nextCursor' | 'hasMore' | 'rankingVersion'>,
): void {
	const id = activeSessionIds.get(root);
	const session = id ? sessions.get(id) : null;
	if (!session) return;
	session.topics = cloneTopics(input.topics);
	session.nextCursor = input.nextCursor;
	session.hasMore = input.hasMore;
	session.rankingVersion = input.rankingVersion;
}

/** Keeps the active Topic-return snapshot in sync with a completed reaction write. */
export function patchActiveBrowseTopicViewerState(root: Root, topicId: string, patch: Pick<PublicTopic, 'isFavorite' | 'voteValue'>): BrowsePaginationSession | null {
	const id = activeSessionIds.get(root);
	const session = id ? sessions.get(id) : null;
	if (!session || !session.topics.some((topic) => topic.slug === topicId)) return null;
	session.topics = session.topics.map((topic) => topic.slug === topicId ? { ...topic, ...patch } : topic);
	return cloneSession(session);
}

export function markBrowseTopicDeparture(root: Root, scopeKey: string, scrollY: number, now = Date.now()): BrowseReturnSessionMarker | null {
	pruneExpired(now);
	const id = activeSessionIds.get(root);
	const session = id ? sessions.get(id) : null;
	if (!session || session.scopeKey !== scopeKey) return null;
	session.scrollY = Math.max(0, scrollY);
	return { id: session.id, scopeKey, capturedAt: now };
}

export function consumeBrowseReturnSessionFromHistory(scopeKey: string, now = Date.now()): BrowsePaginationSession | null {
	if (typeof window === 'undefined' || !window.history) return null;
	const state = window.history.state;
	const stateEntryId = readBrowseHistoryEntryId(state);
	const liveEntry = stateEntryId ? historyEntries.get(stateEntryId) : undefined;
	const marker = liveEntry ? liveEntry.marker : parseMarker(state && typeof state === 'object' ? (state as Record<string, unknown>).browseReturnSession : undefined);
	if (!marker || marker.scopeKey !== scopeKey) return null;
	if (consumedReturnMarkers.has(returnMarkerKey(marker))) return null;
	pruneExpired(now);
	const session = sessions.get(marker.id);
	if (!session || session.scopeKey !== scopeKey) return null;
	// A return marker represents one specific Topic → Browse hand-off. Consume
	// both copies synchronously before mounting the retained pager so a second
	// hydration in the same popstate cannot rebuild the list from page one.
	consumeBrowseReturnMarker(state, stateEntryId, liveEntry);
	consumedReturnMarkers.add(returnMarkerKey(marker));
	return cloneSession(session);
}

export function activateBrowsePaginationSession(root: Root, sessionId: string): void {
	if (sessions.has(sessionId)) activeSessionIds.set(root, sessionId);
}

export function readBrowseHistorySnapshot(state: unknown): BrowseHistorySnapshot | null {
	if (!state || typeof state !== 'object' || Array.isArray(state)) return null;
	const stateEntryId = readBrowseHistoryEntryId(state);
	const liveEntry = stateEntryId ? historyEntries.get(stateEntryId) : undefined;
	if (liveEntry) return { ...liveEntry.snapshot };
	const snapshot = (state as Record<string, unknown>).browseSnapshot;
	if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return null;
	const value = snapshot as Record<string, unknown>;
	const entryId = typeof value.entryId === 'string' && value.entryId.length > 0 ? value.entryId : undefined;
	if (
		typeof value.filter !== 'string' || value.filter.length === 0
		|| typeof value.search !== 'string'
		|| (value.sort !== 'default' && value.sort !== 'trending' && value.sort !== 'newest')
		|| typeof value.scrollY !== 'number' || !Number.isFinite(value.scrollY)
		|| (entryId !== undefined && stateEntryId !== undefined && entryId !== stateEntryId)
	) return null;
	return { entryId, filter: value.filter, search: value.search, sort: value.sort, scrollY: Math.max(0, value.scrollY) };
}

export function syncCurrentBrowseHistoryEntry(
	root: Root,
	runtime: PrototypeRuntime,
	filter: string,
	search: string,
	sort: BrowseSort,
	scrollY = window.scrollY,
	options: { preserveReturnMarker?: boolean } = {},
): void {
	const record = recordBrowseHistorySnapshot(root, runtime, filter, search, sort, scrollY, options);
	if (!record) return;
	try {
		window.history.replaceState(record.state, '', window.location.href);
	} catch (error) {
		// Safari can rate-limit History writes while a long list is scrolling.
		// The in-memory snapshot above is the return-session truth; browser
		// serialization is only a best-effort persistence layer.
		if (isHistoryRateLimitError(error)) return;
		throw error;
	}
}

/** Updates the active Browse return snapshot without serializing browser history. */
export function recordBrowseHistorySnapshot(
	root: Root,
	runtime: PrototypeRuntime,
	filter: string,
	search: string,
	sort: BrowseSort,
	scrollY = window.scrollY,
	options: { preserveReturnMarker?: boolean } = {},
): { state: Record<string, unknown> } | null {
	if (typeof window === 'undefined' || !window.history || window.location.pathname.replace(/\/+$/u, '') !== '/browse') return null;
	const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state as Record<string, unknown> : {};
	const entryId = readBrowseHistoryEntryId(currentState);
	if (!entryId) return null;
	const snapshot: BrowseHistorySnapshot = { entryId, filter, search, sort, scrollY: Math.max(0, scrollY) };
	const scopeKey = browsePaginationScopeKey(runtime, filter, search, sort);
	const liveMarker = historyEntries.get(entryId)?.marker;
	const serializedMarker = parseMarker(currentState.browseReturnSession);
	const preservedMarker = options.preserveReturnMarker ? liveMarker ?? serializedMarker : null;
	// Scroll/filter snapshots describe the current Browse view only. A Topic
	// return marker is minted exclusively by the Browse-card action path; doing
	// it here recreates an already-consumed marker during double-rAF scroll
	// restoration and incorrectly turns any Browse departure into a Topic return.
	const marker = preservedMarker?.scopeKey === scopeKey ? preservedMarker : null;
	historyEntries.set(entryId, { snapshot, marker, updatedAt: Date.now() });
	pruneHistoryEntries();
	const { browseReturnSession: _previousMarker, ...stateWithoutPreviousMarker } = currentState;
	return { state: {
		...stateWithoutPreviousMarker,
		browseHistoryEntryId: entryId,
		browseSnapshot: snapshot,
		...(marker ? { browseReturnSession: marker } : {}),
	} };
}

function isHistoryRateLimitError(error: unknown): boolean {
	return error instanceof DOMException && error.name === 'SecurityError';
}

export function createBrowseHistoryEntryId(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return `browse-history-${crypto.randomUUID()}`;
	fallbackId += 1;
	return `browse-history-${Date.now()}-${fallbackId}`;
}

/** Creates a fresh Browse history entry without inheriting another entry's restored state. */
export function createBrowseHistoryEntryState(currentState: unknown): Record<string, unknown> {
	const state = currentState && typeof currentState === 'object' && !Array.isArray(currentState)
		? currentState as Record<string, unknown>
		: {};
	const { browseHistoryEntryId: _entryId, browseSnapshot: _snapshot, browseReturnSession: _marker, ...stateWithoutBrowseHistory } = state;
	return { ...stateWithoutBrowseHistory, view: 'home-browse', browseHistoryEntryId: createBrowseHistoryEntryId() };
}

export function hasBrowseReturnSessionMarker(state: unknown): boolean {
	if (!state || typeof state !== 'object' || Array.isArray(state)) return false;
	const entryId = readBrowseHistoryEntryId(state);
	const liveEntry = entryId ? historyEntries.get(entryId) : undefined;
	return Boolean(liveEntry ? liveEntry.marker : parseMarker((state as Record<string, unknown>).browseReturnSession));
}

function consumeBrowseReturnMarker(
	state: unknown,
	entryId: string | null,
	liveEntry: { snapshot: BrowseHistorySnapshot; marker: BrowseReturnSessionMarker | null; updatedAt: number } | undefined,
): void {
	if (entryId && liveEntry) historyEntries.set(entryId, { ...liveEntry, marker: null, updatedAt: Date.now() });
	if (!state || typeof state !== 'object' || Array.isArray(state)) return;
	const { browseReturnSession: _marker, ...withoutMarker } = state as Record<string, unknown>;
	try {
		window.history.replaceState(withoutMarker, '');
	} catch (error) {
		// The live entry above is already consumed, so a Safari History-write
		// throttle cannot trigger a duplicate hydrate in this document.
		if (!isHistoryRateLimitError(error)) throw error;
	}
}

export function clearBrowsePaginationSessions(): void {
	sessions.clear();
	historyEntries.clear();
	consumedReturnMarkers.clear();
	activeSessionIds = new WeakMap<Root, string>();
}

export function discardActiveBrowsePaginationSession(root: Root): void {
	const id = activeSessionIds.get(root);
	if (id) sessions.delete(id);
	activeSessionIds.delete(root);
}

export function resetBrowsePaginationSessionsForTest(): void {
	clearBrowsePaginationSessions();
	fallbackId = 0;
}

function parseMarker(value: unknown): BrowseReturnSessionMarker | null {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
	const marker = value as Record<string, unknown>;
	if (Object.keys(marker).length !== 3 || typeof marker.id !== 'string' || typeof marker.scopeKey !== 'string' || typeof marker.capturedAt !== 'number') return null;
	if (!marker.id || !marker.scopeKey || !Number.isFinite(marker.capturedAt)) return null;
	return { id: marker.id, scopeKey: marker.scopeKey, capturedAt: marker.capturedAt };
}

function returnMarkerKey(marker: BrowseReturnSessionMarker): string {
	return `${marker.id}\u0000${marker.scopeKey}\u0000${marker.capturedAt}`;
}

function readBrowseHistoryEntryId(state: unknown): string | null {
	if (!state || typeof state !== 'object' || Array.isArray(state)) return null;
	const entryId = (state as Record<string, unknown>).browseHistoryEntryId;
	return typeof entryId === 'string' && entryId.length > 0 ? entryId : null;
}

function pruneExpired(now: number): void {
	for (const [id, session] of sessions) {
		if (now >= session.startedAt + TTL_MS) sessions.delete(id);
	}
}

function pruneOverflow(): void {
	if (sessions.size <= MAX_SESSIONS) return;
	const oldest = [...sessions.values()].sort((a, b) => a.startedAt - b.startedAt).slice(0, sessions.size - MAX_SESSIONS);
	for (const session of oldest) sessions.delete(session.id);
}

function pruneHistoryEntries(): void {
	if (historyEntries.size <= MAX_HISTORY_ENTRIES) return;
	const oldest = [...historyEntries.entries()].sort(([, a], [, b]) => a.updatedAt - b.updatedAt).slice(0, historyEntries.size - MAX_HISTORY_ENTRIES);
	for (const [entryId] of oldest) historyEntries.delete(entryId);
}

function nextSessionId(): string {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
	fallbackId += 1;
	return `browse-session-${Date.now()}-${fallbackId}`;
}

function cloneSession(session: BrowsePaginationSession): BrowsePaginationSession {
	return { ...session, topics: cloneTopics(session.topics) };
}

function cloneTopics(topics: PublicTopic[]): PublicTopic[] {
	return topics.map((topic) => ({
		...topic,
		tags: topic.tags ? [...topic.tags] : undefined,
		engagement: topic.engagement ? { ...topic.engagement } : undefined,
	}));
}
